<script lang="ts" setup>
import { computed, defineProps } from 'vue';
import CircularProgress from '@/components/CircularProgress.vue';
import PercentageBadgeWithLink from '@/components/Badge/PercentageBadgeWithLink.vue';
import { CampaignsStatistics } from '@/api/campaigns';
import { useRoute } from 'vue-router/composables';
import { usePanelStore } from '@/store/pinia/panel';
import ProspectsPanel from '@/components/Prospects/ProspectsPanel.vue';
import { CampaignProspect } from '@/domains/models/Campaign';
import defaultAvatar from '@/assets/img/default-avatar.svg';
import BcAvatar from '@/ui-kit/components/BcAvatar/BcAvatar.vue';
import MvIcon from '@/lib/icons/components/MvIcon.vue';
import ProspectsCardStatus from '@/components/Prospects/ProspectsCardStatus.vue';
import DateBadgeWithTooltip from '@/components/Badge/DateBadgeWithTooltip.vue';

function setAlternativeImg(event: Event) {
  const target = event.target as HTMLImageElement;
  target.src = defaultAvatar;
}
const props = defineProps<{
  prospect: CampaignProspect & { messages: { lastSentMessageId?: string, receivedMessageId?: string, customInvitationMessageId?: string } };
  prospectType: 'contacted' | 'accepted' | 'responded';
}>();

const emit = defineEmits(['view-message']);

const viewMessage = () => {
  emit('view-message');
};

const hasMessage = computed(() => {
  if (props.prospectType === 'contacted') {
    return props.prospect.messages?.lastSentMessageId;
  }
  if (props.prospectType === 'accepted') {
    return props.prospect.messages?.customInvitationMessageId;
  }
  if (props.prospectType === 'responded') {
    return props.prospect.messages?.receivedMessageId;
  }
  return false;
});
</script>

<template>
  <div class="grid grid-cols-subgrid grid-cols-[4fr_3fr_3fr_3fr] col-span-full">
    <div class="grid-cell flex justify-center items-center gap-[10px] text-secondary-dark min-w-0">
      <bc-avatar :src="prospect.coder.pictureUrl" class="card-coders__avatar shrink-0" size="s"
        @error="setAlternativeImg">
      </bc-avatar>
      <div class="flex flex-col items-start min-w-0">
        <p v-tooltip="`${prospect.coder.firstName} ${prospect.coder.lastName}`"
          class="font-medium text-base truncate w-full">
          {{ `${prospect.coder.firstName} ${prospect.coder.lastName}` }}
        </p>
        <div v-if="hasMessage" class="flex items-center gap-1 text-blue-500 hover:text-blue-600 cursor-pointer"
          @click="viewMessage">
          <MvIcon icon="eye" /> Message
        </div>
      </div>
    </div>
    <div class="grid-cell flex justify-center">
      <ProspectsCardStatus :prospect="prospect" />
    </div>
    <div class="grid-cell flex justify-center text-sm items-center font-bold text-blue-900"
      v-tooltip="prospect.campaign.name">
      <span class="line-clamp-2 truncate text-wrap">{{ prospect.campaign.name }}</span>
    </div>
    <div class="grid-cell flex justify-center">
      <DateBadgeWithTooltip :date="prospect.createdAt" withHHMM />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.grid-cell {
  @apply px-2.5 py-1 h-[3.5rem] flex items-center justify-start overflow-hidden;
}
</style>